import { getLenis } from '../lib/lenis';

// Any link pointing at #demo opens the video modal instead of scrolling to the
// dashboard section. Menu links come out of WordPress as absolute URLs, so the
// hash is read from the resolved href rather than the raw attribute.
//
// The listener runs in the capture phase so it wins over modules/anchors.ts,
// which bails on an already-prevented event. If the modal isn't on the page
// (no video URL entered), nothing is intercepted and #demo still scrolls.
const TRIGGER_HASH = 'demo';

function hashOf(link: HTMLAnchorElement): string {
  const href = link.getAttribute('href') || '';
  if (href.startsWith('#')) return href.slice(1);

  try {
    return new URL(link.href, window.location.href).hash.slice(1);
  } catch {
    return '';
  }
}

function play(player: HTMLElement, src: string): void {
  if (!player.getAttribute('src')) player.setAttribute('src', src);

  if (player instanceof HTMLVideoElement) void player.play().catch(() => undefined);
}

// Clearing the source is what actually stops playback — an iframe keeps playing
// behind a closed dialog otherwise.
function stop(player: HTMLElement): void {
  if (player instanceof HTMLVideoElement) {
    player.pause();
    player.removeAttribute('src');
    player.load();
    return;
  }

  player.removeAttribute('src');
}

export function initVideoModal(): void {
  const modal = document.querySelector<HTMLDialogElement>('[data-video-modal]');
  if (!modal || typeof modal.showModal !== 'function') return;

  const src = modal.dataset.videoUrl;
  const player = modal.querySelector<HTMLElement>('.video-modal__player');
  if (!player || !src) return;

  const closeButton = modal.querySelector<HTMLButtonElement>('[data-video-modal-close]');

  const open = (): void => {
    play(player, src);
    modal.showModal();
    // A cross-origin player iframe swallows Esc once it takes focus, so the
    // close button starts focused and stays the keyboard way out.
    closeButton?.focus();
    getLenis()?.stop();
  };

  modal.addEventListener('close', () => {
    stop(player);
    getLenis()?.start();
  });

  closeButton?.addEventListener('click', () => modal.close());

  // Belt and braces next to the dialog's own Esc handling, which some browsers
  // skip while an embedded player holds focus.
  modal.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      modal.close();
    }
  });

  // Clicking the backdrop lands on the dialog itself, never on the panel.
  modal.addEventListener('click', (event) => {
    if (event.target === modal) modal.close();
  });

  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;

      const link = (event.target as HTMLElement | null)?.closest?.<HTMLAnchorElement>('a[href]');
      if (!link || link.target === '_blank' || hashOf(link) !== TRIGGER_HASH) return;

      event.preventDefault();
      open();
    },
    { capture: true },
  );
}
